import React, { useEffect, useRef, useState } from 'react';
import {
  listTickerGroups,
  createTickerGroup,
  renameOrUpdateTickerGroup,
  removeTickerGroup,
} from './scoringPresetStore';

/**
 * Ticker chips + ticker-group save/load. Sits at the top of the page so the
 * user always knows which stocks they're scoring.
 */
function TickerSection({ tickers, onTickersChange }) {
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [savePromptOpen, setSavePromptOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveMode, setSaveMode] = useState('new'); // 'new' | 'overwrite'
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { reloadGroups(); }, []);

  const reloadGroups = () => listTickerGroups()
    .then(setGroups)
    .catch((e) => setError(formatError(e, 'Could not load ticker groups')));

  const addTicker = (raw) => {
    const t = String(raw || '').trim().toUpperCase();
    if (!t) return;
    if (tickers.includes(t)) return;
    onTickersChange([...tickers, t]);
  };

  const removeTicker = (t) => {
    onTickersChange(tickers.filter((x) => x !== t));
    // If we had a group loaded, viewing it diverged — clear the selection.
    if (selectedGroupId) setSelectedGroupId('');
  };

  const handleInputKey = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      const parts = adding.split(/[\s,]+/).filter(Boolean);
      for (const p of parts) addTicker(p);
      setAdding('');
    } else if (e.key === 'Backspace' && !adding && tickers.length) {
      removeTicker(tickers[tickers.length - 1]);
    }
  };

  const handleLoadGroup = (id) => {
    setSelectedGroupId(id);
    setError(null);
    if (!id) return;
    const g = groups.find((x) => String(x.id) === String(id));
    if (g) onTickersChange(g.tickers || []);
  };

  const openSavePrompt = () => {
    if (!tickers.length) {
      setError('Add at least one ticker first.');
      return;
    }
    setError(null);
    if (selectedGroupId) {
      // Loaded group is selected — offer to overwrite or save-as-new
      const g = groups.find((x) => String(x.id) === String(selectedGroupId));
      setSaveName(g?.name || '');
      setSaveMode('overwrite');
    } else {
      setSaveMode('new');
      setSaveName('');
    }
    setSavePromptOpen(true);
  };

  const handleSaveSubmit = async () => {
    setError(null);
    const name = saveName.trim();
    if (!name) { setError('Group name required.'); return; }
    try {
      if (saveMode === 'overwrite' && selectedGroupId) {
        const updated = await renameOrUpdateTickerGroup(Number(selectedGroupId), name, tickers);
        setGroups((gs) => gs.map((g) => (g.id === updated.id ? updated : g)));
        setSelectedGroupId(String(updated.id));
      } else {
        const created = await createTickerGroup(name, tickers);
        setGroups((gs) => [created, ...gs]);
        setSelectedGroupId(String(created.id));
      }
      setSavePromptOpen(false);
    } catch (e) {
      setError(formatError(e, 'Could not save group'));
    }
  };

  const handleDeleteGroup = async () => {
    if (!selectedGroupId) return;
    const g = groups.find((x) => String(x.id) === String(selectedGroupId));
    if (!g) return;
    if (!window.confirm(`Delete ticker group "${g.name}"?`)) return;
    try {
      await removeTickerGroup(g.id);
      setGroups((gs) => gs.filter((x) => x.id !== g.id));
      setSelectedGroupId('');
    } catch (e) {
      setError(formatError(e, 'Could not delete group'));
    }
  };

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm text-gray-300">
          <span className="font-semibold text-white">Tickers ({tickers.length})</span>
          <span className="ml-2 text-xs text-gray-500">scoring will run on this list</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={selectedGroupId}
            onChange={(e) => handleLoadGroup(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-white"
          >
            <option value="">— Load saved group —</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name} ({(g.tickers || []).length})
              </option>
            ))}
          </select>
          {selectedGroupId && (
            <button
              type="button"
              onClick={handleDeleteGroup}
              className="px-2 py-1 text-xs rounded border border-red-700/60 text-red-300 hover:bg-red-900/30 transition"
              title="Delete this ticker group"
            >
              Delete group
            </button>
          )}
          <button
            type="button"
            onClick={openSavePrompt}
            className="px-2 py-1 text-xs rounded border border-blue-500/60 text-blue-300 hover:bg-blue-600/20 transition"
          >
            {selectedGroupId ? 'Save / overwrite group' : 'Save as group'}
          </button>
        </div>
      </div>

      {/* Chips + input */}
      <div
        onClick={() => inputRef.current?.focus()}
        className="min-h-[44px] bg-gray-800 border border-gray-700 rounded px-2 py-1.5 flex flex-wrap items-center gap-1.5 cursor-text focus-within:border-blue-500"
      >
        {tickers.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 bg-blue-500/20 border border-blue-500/40 rounded px-2 py-0.5 text-sm text-blue-100 font-mono"
          >
            {t}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeTicker(t); }}
              className="text-blue-300 hover:text-red-300"
              title={`Remove ${t}`}
            >×</button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={adding}
          onChange={(e) => setAdding(e.target.value.toUpperCase())}
          onKeyDown={handleInputKey}
          onBlur={() => {
            const parts = adding.split(/[\s,]+/).filter(Boolean);
            for (const p of parts) addTicker(p);
            setAdding('');
          }}
          placeholder={tickers.length ? '' : 'Type a ticker, then Enter…'}
          className="flex-1 min-w-[120px] bg-transparent text-white text-sm font-mono outline-none placeholder-gray-500"
        />
      </div>

      {/* Save prompt */}
      {savePromptOpen && (
        <div className="bg-gray-800 border border-blue-500/40 rounded p-3 space-y-2">
          <div className="text-sm text-white">
            {saveMode === 'overwrite' ? 'Overwrite group with current tickers' : 'Save current tickers as a new group'}
          </div>
          {saveMode === 'overwrite' && (
            <div className="flex items-center gap-3 text-xs">
              <label className="flex items-center gap-1 text-gray-300">
                <input
                  type="radio"
                  checked={saveMode === 'overwrite'}
                  onChange={() => setSaveMode('overwrite')}
                />
                Overwrite existing
              </label>
              <label className="flex items-center gap-1 text-gray-300">
                <input
                  type="radio"
                  checked={saveMode === 'new'}
                  onChange={() => setSaveMode('new')}
                />
                Save as new
              </label>
            </div>
          )}
          <input
            autoFocus
            type="text"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveSubmit(); }}
            placeholder="Group name (e.g. Mega Tech)"
            className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm text-white"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setSavePromptOpen(false)}
              className="px-3 py-1 text-xs rounded border border-gray-600 text-gray-200 hover:bg-gray-700 transition"
            >Cancel</button>
            <button
              type="button"
              onClick={handleSaveSubmit}
              className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white transition"
            >Save</button>
          </div>
        </div>
      )}

      {error && (
        <div className="text-xs text-red-300 whitespace-pre-wrap">{error}</div>
      )}
    </div>
  );
}

function formatError(err, fallback) {
  if (!err) return fallback;
  const data = err.response?.data;
  if (data && typeof data === 'object') {
    const lines = [];
    for (const [k, v] of Object.entries(data)) {
      if (Array.isArray(v)) lines.push(`${k}: ${v.join('; ')}`);
      else lines.push(`${k}: ${String(v)}`);
    }
    if (lines.length) return lines.join('\n');
  }
  return err.message || fallback;
}

export default TickerSection;
